import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ShieldCheck, ShieldOff, Key, Copy, Check, Loader2, Lock, AlertTriangle, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ChangePasswordCard({ getHeaders, hdrs }) {
  const { t } = useTranslation('dashboard');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMsg(''); setError('');
    if (newPw.length < 8) return setError(t('security.pwTooShort'));
    if (newPw !== confirmPw) return setError(t('security.pwMismatch'));
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/change-password`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('security.pwFailed'));
      setMsg(t('security.pwSuccess'));
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-gray-900 flex items-center gap-2">
          <Lock className="h-5 w-5 text-blue-400" />
          {t('security.changePassword')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" data-testid="pw-error">{error}</div>}
          {msg && <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm" data-testid="pw-success">{msg}</div>}
          <div className="space-y-1">
            <Label className="text-xs text-gray-600">{t('security.currentPassword')}</Label>
            <div className="relative max-w-sm">
              <Input type={showCurrent ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} required placeholder={t('security.currentPlaceholder')} className="pr-10 bg-gray-50 border-gray-200 text-gray-900" data-testid="current-password" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-600">{t('security.newPassword')}</Label>
            <div className="relative max-w-sm">
              <Input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} required placeholder={t('security.newPlaceholder')} minLength={8} className="pr-10 bg-gray-50 border-gray-200 text-gray-900" data-testid="new-password" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-gray-600">{t('security.confirmNewPassword')}</Label>
            <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required placeholder={t('security.confirmPlaceholder')} className="max-w-sm bg-gray-50 border-gray-200 text-gray-900" data-testid="confirm-password" />
          </div>
          <Button type="submit" disabled={saving || !currentPw || !newPw || !confirmPw} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="change-password-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            {t('security.changePassword')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function ClientSecuritySettings({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('status'); // status | setup | backup | disable
  const [qrData, setQrData] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const hdrs = useCallback(() => ({
    ...getHeaders(),
    'Content-Type': 'application/json',
  }), [getHeaders]);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/2fa/status`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setTwoFaEnabled(d.enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const startSetup = async () => {
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/2fa/setup`, {
        method: 'POST', headers: hdrs(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Setup failed');
      setQrData(data);
      setView('setup');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmSetup = async () => {
    if (setupCode.length < 6) return;
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/2fa/confirm`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ code: setupCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      setBackupCodes(data.backup_codes || []);
      setTwoFaEnabled(true);
      setView('backup');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const disable2FA = async () => {
    if (!disablePassword) return;
    setError('');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/client/dashboard/2fa/disable`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ password: disablePassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to disable');
      setTwoFaEnabled(false);
      setView('status');
      setDisablePassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-12">Loading security settings...</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="security-settings">
      {/* Change Password Card */}
      <ChangePasswordCard getHeaders={getHeaders} hdrs={hdrs} />

      {/* 2FA Status Card */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-400" />
            {t('security.twoFa')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-700 text-red-300 px-3 py-2 rounded text-sm mb-4" data-testid="2fa-error">
              {error}
            </div>
          )}

          {/* STATUS VIEW */}
          {view === 'status' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3">
                  {twoFaEnabled ? (
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-green-400" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-amber-900/30 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-amber-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {twoFaEnabled ? '2FA is enabled' : '2FA is not enabled'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {twoFaEnabled
                        ? 'Your account is protected with an authenticator app'
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                </div>
                <Badge className={twoFaEnabled ? 'bg-green-50 text-green-400' : 'bg-amber-900/30 text-amber-400'} data-testid="2fa-status-badge">
                  {twoFaEnabled ? t('security.enabled') : t('security.disabled')}
                </Badge>
              </div>

              {!twoFaEnabled ? (
                <Button onClick={startSetup} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="2fa-enable-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  {t('security.enable')}
                </Button>
              ) : (
                <Button onClick={() => { setView('disable'); setError(''); }} variant="outline"
                  className="border-red-800 text-red-400 hover:bg-red-900/20 hover:text-red-300" data-testid="2fa-disable-start-btn">
                  <ShieldOff className="h-4 w-4 mr-2" /> {t('security.disable')}
                </Button>
              )}

              <div className="bg-blue-900/10 border border-blue-500/20 rounded-lg px-4 py-3">
                <p className="text-xs text-blue-300">
                  Two-factor authentication adds a second verification step when you sign in. You'll need an authenticator app like Google Authenticator, Authy, or 1Password to generate time-based codes.
                </p>
              </div>
            </div>
          )}

          {/* SETUP VIEW */}
          {view === 'setup' && qrData && (
            <div className="space-y-4">
              <p className="text-sm text-gray-300">{t('security.scanQr')}</p>
              <div className="flex justify-center p-4 bg-white rounded-lg w-fit mx-auto">
                <img src={`data:image/png;base64,${qrData.qr_code}`} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 mb-1">{t('security.verifyCode')}</p>
                <code className="text-xs bg-gray-50 text-green-400 px-3 py-1.5 rounded font-mono select-all" data-testid="2fa-manual-key">
                  {qrData.secret}
                </code>
              </div>
              <div className="space-y-2 pt-2">
                <Label className="text-xs text-gray-400">{t('security.verifyCode')}</Label>
                <Input
                  value={setupCode}
                  onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6}
                  className="bg-gray-50 border-gray-200 text-gray-900 text-center text-xl tracking-[0.3em] font-mono max-w-xs mx-auto"
                  autoFocus data-testid="2fa-setup-code"
                />
              </div>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => { setView('status'); setError(''); setSetupCode(''); }}
                  className="border-gray-200 text-gray-400">{t('security.cancel')}</Button>
                <Button onClick={confirmSetup} disabled={saving || setupCode.length < 6} className="bg-blue-600 hover:bg-blue-700" data-testid="2fa-confirm-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  {t('security.verifyAndEnable')}
                </Button>
              </div>
            </div>
          )}

          {/* BACKUP CODES VIEW */}
          {view === 'backup' && (
            <div className="space-y-4">
              <div className="bg-green-900/20 border border-green-700/30 rounded-lg px-4 py-3" data-testid="2fa-success">
                <p className="text-sm text-green-400 font-medium">{t('security.enabled')}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-900 font-medium flex items-center gap-2">
                    <Key className="h-4 w-4 text-amber-400" /> {t('security.backupCodes')}
                  </p>
                  <Button size="sm" variant="outline" onClick={copyBackupCodes}
                    className="border-gray-200 text-gray-400 text-xs h-7" data-testid="copy-backup-codes">
                    {copied ? <Check className="h-3 w-3 mr-1 text-green-400" /> : <Copy className="h-3 w-3 mr-1" />}
                    {copied ? t('security.copied') : t('security.copyCodes')}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mb-3">{t('security.backupHint')}</p>
                <div className="grid grid-cols-2 gap-2" data-testid="backup-codes-grid">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-xs text-gray-300 text-center select-all">
                      {code}
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={() => setView('status')} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="2fa-done-btn">
                {t('security.doneBackup')}
              </Button>
            </div>
          )}

          {/* DISABLE VIEW */}
          {view === 'disable' && (
            <div className="space-y-4">
              <div className="bg-red-900/20 border border-red-700/30 rounded-lg px-4 py-3">
                <p className="text-sm text-red-400">{t('security.disableHint')}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-400">{t('security.password')}</Label>
                <div className="relative max-w-xs">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={disablePassword}
                    onChange={e => setDisablePassword(e.target.value)}
                    placeholder={t('security.currentPlaceholder')}
                    className="pl-10 pr-10 bg-gray-50 border-gray-200 text-gray-900"
                    data-testid="2fa-disable-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setView('status'); setError(''); setDisablePassword(''); }}
                  className="border-gray-200 text-gray-400">{t('security.cancel')}</Button>
                <Button onClick={disable2FA} disabled={saving || !disablePassword}
                  className="bg-red-600 hover:bg-red-700" data-testid="2fa-disable-confirm-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldOff className="h-4 w-4 mr-2" />}
                  {t('security.disableConfirm')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
